/**
 * Snapshot store for the file-agent's undo trail.
 *
 * Pre-edit file contents are stored before every write / delete so the
 * user can undo any change the agent made. The IndexedDB connection
 * lives in {@link folder-library} so all stores share the same upgrade
 * lifecycle and we don't end up with two competing version numbers.
 *
 * Legacy chatId-keyed `handles` records are migrated into the folder
 * library on first read by {@link migrateLegacyChatHandle}; the helpers
 * that wrote them are no longer needed.
 *
 * @module file-agent/lib/fs-handle-store
 */

import { awaitReq, openFileAgentDb, SNAPSHOT_STORE } from "./folder-library";

interface SnapshotRecord {
  /** Composite key: `${chatId}:${snapshotId}`. */
  key: string;
  chatId: string;
  /** Tool call id this snapshot belongs to (one snapshot per edit). */
  snapshotId: string;
  path: string;
  /** "write" | "create" | "delete" — matches the tool that produced it. */
  kind: "write" | "create" | "delete";
  /** Previous content (null for create — the file did not exist before). */
  previousContent: string | null;
  createdAt: number;
}

/** Insert a pre-edit snapshot. */
export async function saveSnapshot(
  record: Omit<SnapshotRecord, "key" | "createdAt">,
): Promise<void> {
  const db = await openFileAgentDb();
  const full: SnapshotRecord = {
    ...record,
    key: `${record.chatId}:${record.snapshotId}`,
    createdAt: Date.now(),
  };
  await awaitReq(
    db.transaction(SNAPSHOT_STORE, "readwrite")
      .objectStore(SNAPSHOT_STORE)
      .put(full),
  );
}

/** Retrieve a single snapshot for undo. */
export async function getSnapshot(
  chatId: string,
  snapshotId: string,
): Promise<SnapshotRecord | null> {
  const db = await openFileAgentDb();
  const record = await awaitReq<SnapshotRecord | undefined>(
    db.transaction(SNAPSHOT_STORE, "readonly")
      .objectStore(SNAPSHOT_STORE)
      .get(`${chatId}:${snapshotId}`),
  );
  return record ?? null;
}

/** Remove a snapshot once it has been used (or is no longer needed). */

export type { SnapshotRecord };
